import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import './fp3-dashboard';
import './fp3-login';
import './fp3-sales';
import './lph-nav';
import type { AppSection } from './lph-nav';
import { fp3SiteConfig } from './site-config';

interface SessionUser {
  email?: string;
  name?: string;
  role?: string;
  phone?: string;
}

@customElement('fp3-app')
export class Fp3App extends LitElement {
  @state() private user: SessionUser | null = null;
  @state() private ready = false;
  @state() private view: AppSection = 'sales';

  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
      width: 100%;
      font-family: 'DM Sans', system-ui, -apple-system, sans-serif;
      background: #0f1419;
      color: #e8edf2;
    }
    header {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem clamp(1rem, 4vw, 2rem);
      border-bottom: 1px solid #1e2a36;
      background: #121a22;
      flex-shrink: 0;
    }
    main {
      flex: 1;
      width: 100%;
      max-width: none;
      margin: 0 auto;
      padding: clamp(1rem, 3vw, 2rem);
      box-sizing: border-box;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('fp3-unauthorized', this.onUnauthorized);
    this.loadUser();
  }

  disconnectedCallback() {
    window.removeEventListener('fp3-unauthorized', this.onUnauthorized);
    super.disconnectedCallback();
  }

  private onUnauthorized = () => {
    this.user = null;
    this.ready = true;
    void fetch('/api/auth/logout', { method: 'POST' });
  };

  async loadUser() {
    try {
      const res = await fetch('/api/me');
      this.user = res.ok ? await res.json() : null;
    } catch {
      this.user = null;
    } finally {
      this.ready = true;
    }
  }

  private signedIn(event: CustomEvent<SessionUser>) {
    this.user = event.detail;
    this.view = 'sales';
  }

  private async signOut() {
    await fetch('/api/auth/logout', { method: 'POST' });
    this.user = null;
    window.location.assign(fp3SiteConfig.logoutUrl);
  }

  render() {
    const admin = this.user?.role === 'admin';
    return html`
      <header>
        <lph-nav
          .config=${fp3SiteConfig}
          email=${this.user?.email ?? ''}
          role=${this.user?.role ?? ''}
          phone=${this.user?.phone ?? ''}
          section=${this.view}
          @navigate=${(event: CustomEvent<AppSection>) => { this.view = event.detail; }}
          @sign-out=${this.signOut}
        ></lph-nav>
      </header>
      <main @signed-in=${this.signedIn}>
        ${!this.ready
          ? html``
          : !this.user
            ? html`<fp3-login></fp3-login>`
            : html`
              ${this.view === 'directory'
                ? html`<fp3-dashboard></fp3-dashboard>`
                : html`<fp3-sales ?admin=${admin}></fp3-sales>`}
            `}
      </main>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fp3-app': Fp3App;
  }
}
